import { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import { FaCreditCard, FaMoneyBillWave, FaKeyboard } from "react-icons/fa";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import { shiftActions } from "../../sagas/shiftSlice";
import { useDispatch } from "react-redux";
import { formatPrice } from "../../helpers/utility";
import "./CloseShiftModal.css";

const differenceText = (difference) => {
  if (difference === 0) return 'Le montant correspond.';
  return difference < 0
    ? `Il manque ${formatPrice(Math.abs(difference))}`
    : `${formatPrice(difference)} en plus`;
};

const PaymentAmount = ({ type, title, icon, expected, value, disabled, onEnter }) => {
  const difference = Math.round((Number(value || 0) - expected) * 100) / 100;
  return (
    <div className="close-shift-payment">
      <h6>{icon} {title}</h6>
      <Label for={`close-shift-${type}`}>
        {type === 'cash' ? 'Argent compté dans la caisse' : 'Total sur le terminal de carte'}
      </Label>
      <Button id={`close-shift-${type}`} color="light" className="close-shift-amount" onClick={onEnter} disabled={disabled}>
        <span>{value === null ? 'Saisir le montant' : formatPrice(value)}</span>
        <FaKeyboard aria-hidden="true" />
      </Button>
      <div className="close-shift-expected"><span>Montant attendu</span><strong>{formatPrice(expected)}</strong></div>
      {value !== null && <div className={`close-shift-difference ${difference === 0 ? 'matches' : difference < 0 ? 'missing' : 'extra'}`} role="status">{differenceText(difference)}</div>}
    </div>
  );
};

const CloseShiftModal = ({ isOpen, toggle, currentShift, onCloseShift, isSubmitting = false }) => {
  const [closingCash, setClosingCash] = useState(null);
  const [closingCard, setClosingCard] = useState(null);
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [numPadOpen, setNumPadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState('cash');
  const confirmationPending = useRef(false);
  const session = useRef(0);
  const dispatch = useDispatch();
  const expectedCash = Number(summary?.expectedCashAmount || 0);
  const expectedCard = Number(summary?.expectedCardAmount || 0);
  const cashRefunds = Number(summary?.totalCashReturns ?? summary?.totalCashReturnsWithoutTicket ?? 0);
  const cardRefunds = Number(summary?.totalCardReturns ?? summary?.totalCardReturnsWithoutTicket ?? 0);
  const busy = isSubmitting || confirming;
  const ready = !loading && !error && summary && closingCash !== null && closingCard !== null;

  useEffect(() => {
    const currentSession = ++session.current;
    let cancelled = false;
    setNumPadOpen(false);
    setClosingCash(null);
    setClosingCard(null);
    setNotes('');
    setSummary(null);
    setError('');
    setLoading(true);
    if (!isOpen) return;
    if (!currentShift?.id) {
      setError('Aucune caisse ouverte.');
      setLoading(false);
      return;
    }
    dispatch(shiftActions.fetchSummary({
      shiftId: currentShift.id,
      onSuccess: (result) => {
        if (cancelled || currentSession !== session.current) return;
        if (!result) {
          setError('Impossible de charger les montants. Réessayez.');
          setLoading(false);
          return;
        }
        setSummary(result);
        // Cash must be counted; zero card payments need no extra entry.
        setClosingCash(result.closingAmountCash == null ? null : Number(result.closingAmountCash));
        setClosingCard(result.closingAmountCard == null
          ? Number(result.expectedCardAmount || 0) === 0 ? 0 : null
          : Number(result.closingAmountCard));
        setLoading(false);
      },
      onError: () => {
        if (cancelled) return;
        setError('Impossible de charger les montants. Réessayez.');
        setLoading(false);
      },
    }));
    return () => { cancelled = true; };
  }, [dispatch, isOpen, currentShift?.id, loadAttempt]);

  const closeModal = () => {
    if (!busy && !confirmationPending.current) toggle();
  };

  const handleConfirm = async () => {
    if (!ready || busy || confirmationPending.current) return;
    const confirmationSession = session.current;
    confirmationPending.current = true;
    setConfirming(true);
    try {
      const { isConfirmed } = await Swal.fire({
        title: 'Fermer la caisse ?',
        text: `Espèces comptées : ${formatPrice(closingCash)}. Carte : ${formatPrice(closingCard)}.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, fermer la caisse',
        cancelButtonText: 'Vérifier les montants',
        confirmButtonColor: '#2563eb',
      });
      if (isConfirmed && confirmationSession === session.current) {
        onCloseShift({ closingCash, closingCard, notes: notes.trim() });
      }
    } finally {
      confirmationPending.current = false;
      setConfirming(false);
    }
  };

  const enterAmount = (type) => {
    if (busy || loading || error) return;
    setKeypadTarget(type);
    setNumPadOpen(true);
  };

  return (
    <Modal isOpen={isOpen} toggle={closeModal} centered size="lg" className="close-shift-modal">
      <ModalHeader toggle={busy ? undefined : closeModal}>
        Fermer la caisse {currentShift?.shiftCode && <small className="close-shift-code">#{currentShift.shiftCode}</small>}
      </ModalHeader>
      <ModalBody>
        {loading ? <div className="close-shift-loading" role="status"><Spinner size="sm" /> Chargement des montants…</div>
          : error ? <Alert color="danger" className="mb-0" role="alert">{error} {currentShift?.id && <Button color="link" size="sm" onClick={() => setLoadAttempt(value => value + 1)}>Réessayer</Button>}</Alert>
          : <>
            <p className="close-shift-help">Comptez l'argent dans la caisse, puis saisissez le montant. Pour la carte, vérifiez le total sur le terminal.</p>
            <Row className="g-2">
              <Col md={6}><PaymentAmount type="cash" title="Espèces" icon={<FaMoneyBillWave aria-hidden="true" />} expected={expectedCash} value={closingCash} disabled={busy} onEnter={() => enterAmount('cash')} /></Col>
              <Col md={6}><PaymentAmount type="card" title="Carte bancaire" icon={<FaCreditCard aria-hidden="true" />} expected={expectedCard} value={closingCard} disabled={busy} onEnter={() => enterAmount('card')} /></Col>
            </Row>
            <details className="close-shift-details">
              <summary>Voir le calcul des montants</summary>
              <div className="table-responsive"><table className="table table-sm mb-0">
                <thead><tr><th scope="col">Montants</th><th scope="col">Espèces</th><th scope="col">Carte</th></tr></thead>
                <tbody>
                  <tr><th scope="row">Au départ</th><td>{formatPrice(summary?.openingAmountCash || 0)}</td><td>{formatPrice(summary?.openingAmountCard || 0)}</td></tr>
                  <tr><th scope="row">Reçu des clients</th><td>{formatPrice(summary?.totalCashPayments || 0)}</td><td>{formatPrice(summary?.totalCardPayments || 0)}</td></tr>
                  {(cashRefunds > 0 || cardRefunds > 0) && <tr><th scope="row">Remboursé aux clients</th><td>{formatPrice(cashRefunds)}</td><td>{formatPrice(cardRefunds)}</td></tr>}
                  <tr><th scope="row">Montant attendu</th><td><strong>{formatPrice(expectedCash)}</strong></td><td><strong>{formatPrice(expectedCard)}</strong></td></tr>
                </tbody>
              </table></div>
            </details>
            <details className="close-shift-details">
              <summary>Ajouter une remarque (facultatif)</summary>
              <Label for="closeShiftNotes" className="visually-hidden">Remarque</Label>
              <Input id="closeShiftNotes" type="textarea" rows={2} value={notes} disabled={busy} onChange={event => setNotes(event.target.value)} placeholder="Exemple : raison du montant manquant" />
            </details>
          </>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline size="sm" onClick={closeModal} disabled={busy}>Retour</Button>
        <Button color="primary" size="sm" onClick={handleConfirm} disabled={!ready || busy}>
          {isSubmitting && <Spinner size="sm" className="me-2" />}
          {isSubmitting ? 'Fermeture en cours…' : 'Fermer la caisse'}
        </Button>
      </ModalFooter>
      <POSNumericKeypadModal isOpen={isOpen && numPadOpen && !busy} toggle={() => setNumPadOpen(false)} title={keypadTarget === 'cash' ? 'Argent compté dans la caisse' : 'Total sur le terminal de carte'} value={keypadTarget === 'cash' ? closingCash : closingCard} allowDecimal maxLength={10} onConfirm={value => {
        const amount = Number(value || 0);
        if (!Number.isFinite(amount) || amount < 0) return;
        if (keypadTarget === 'cash') setClosingCash(amount);
        else setClosingCard(amount);
      }} />
    </Modal>
  );
};

export default CloseShiftModal;
